// /chord-caller — a big-screen flashcard caller: prop a laptop or phone on
// the piano's music stand, the screen calls chords, you play them on the real
// piano. Two screens: a calm dark setup screen (presets + saved decks +
// build-your-own + pace), and the session screen with an enormous chord
// title, self-paced / timed / groove (count-in + metronome) advancing, a hint
// flip (MiniChordDiagram + note names), optional spoken calls, optional MIDI
// auto-advance, wake lock, and fullscreen. Deck logic lives in
// src/lib/chordCaller.js — this file is only chrome around it. Wrong notes
// are never punished: this is a caller, not a judge.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildDeck,
  matchesCard,
  parseSavedDecks,
  MAX_SAVED_DECKS,
  PRESET_DECKS,
  presetConfigForKey,
  presetBlurb,
} from "../lib/chordCaller";
import { KEYS } from "../lib/pianoDrills";
import {
  ensureAudio,
  playTone,
  playToneAt,
  playClickAt,
  audioNow,
} from "../lib/pianoDrillsAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import MiniChordDiagram from "../components/circleOfFifths/MiniChordDiagram";
import { cn } from "../lib/utils";

/* ---------- persisted setup (plain JSON, safe parse) ---------- */

const SETUP_KEY = "chordCallerSetup";

const ALL_DEGREES = [1, 2, 3, 4, 5, 6, 7];

const DEFAULTS = {
  keys: ["C"],
  circle: false,
  content: "chords",
  degrees: ALL_DEGREES,
  inversions: [0, 1, 2],
  progressionText: "1 5 6 4",
  order: "inOrder",
  style: "block",
  bass: false,
  presetKey: "C", // the key the key-scoped presets play in
  paceMode: "self", // "self" | "timer" | "groove"
  paceSeconds: 6,
  grooveBpm: 72,
  grooveBars: 1, // bars per chord in groove mode: 1 | 2
  alwaysHint: false,
  midiAdvance: true,
  strictBass: false,
  speakCalls: false,
};

const loadSetup = () => {
  const out = { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(SETUP_KEY);
    const p = raw ? JSON.parse(raw) : null;
    if (!p || typeof p !== "object") return out;
    if (Array.isArray(p.keys) && p.keys.every((k) => typeof k === "string")) out.keys = p.keys;
    if (typeof p.circle === "boolean") out.circle = p.circle;
    if (typeof p.content === "string") out.content = p.content;
    if (Array.isArray(p.degrees) && p.degrees.every((d) => Number.isInteger(d))) {
      out.degrees = p.degrees;
    }
    if (Array.isArray(p.inversions) && p.inversions.every((i) => Number.isInteger(i))) {
      out.inversions = p.inversions;
    }
    if (typeof p.progressionText === "string") out.progressionText = p.progressionText;
    if (p.order === "inOrder" || p.order === "shuffle") out.order = p.order;
    if (["block", "arpeggio", "ladder"].includes(p.style)) out.style = p.style;
    if (typeof p.bass === "boolean") out.bass = p.bass;
    if (KEYS.some((k) => k.name === p.presetKey)) out.presetKey = p.presetKey;
    if (["self", "timer", "groove"].includes(p.paceMode)) out.paceMode = p.paceMode;
    if (Number.isFinite(p.paceSeconds)) {
      out.paceSeconds = Math.min(15, Math.max(2, Math.round(p.paceSeconds)));
    }
    if (Number.isFinite(p.grooveBpm)) {
      out.grooveBpm = Math.min(140, Math.max(40, Math.round(p.grooveBpm)));
    }
    if (p.grooveBars === 1 || p.grooveBars === 2) out.grooveBars = p.grooveBars;
    if (typeof p.alwaysHint === "boolean") out.alwaysHint = p.alwaysHint;
    if (typeof p.midiAdvance === "boolean") out.midiAdvance = p.midiAdvance;
    if (typeof p.strictBass === "boolean") out.strictBass = p.strictBass;
    if (typeof p.speakCalls === "boolean") out.speakCalls = p.speakCalls;
    return out;
  } catch {
    return out;
  }
};

const saveSetup = (setup) => {
  try {
    window.localStorage.setItem(SETUP_KEY, JSON.stringify(setup));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

/* ---------- saved decks (localStorage, validated by the lib) ---------- */

const DECKS_KEY = "chordCallerDecks";

const loadSavedDecks = () => {
  try {
    return parseSavedDecks(window.localStorage.getItem(DECKS_KEY));
  } catch {
    return [];
  }
};

const saveSavedDecks = (decks) => {
  try {
    window.localStorage.setItem(DECKS_KEY, JSON.stringify(decks));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

// Feature-detect speech once — the "speak the calls" toggle simply doesn't
// render on browsers without SpeechSynthesis.
const speechSupported = typeof window !== "undefined" && "speechSynthesis" in window;

/* ---------- small sounds (page-local, gesture-gated) ---------- */

// Soft blip on each timed card change — quiet and short, not a metronome.
const playTick = () => playTone(98, { durMs: 70, gain: 0.045, type: "sine" });

// Two quick rising notes when MIDI hears the right chord.
const playSuccess = () => {
  const t = audioNow();
  playToneAt(88, t, { durMs: 130, gain: 0.07, type: "sine" });
  playToneAt(95, t + 0.09, { durMs: 170, gain: 0.07, type: "sine" });
};

/* ---------- deck metadata for the setup screen ---------- */

const PRESET_COUNTS = PRESET_DECKS.map((p) => buildDeck(p.config).cards.length);

const CONTENT_OPTIONS = [
  { id: "chords", label: "diatonic chords" },
  { id: "inversions", label: "inversions" },
  { id: "sevenths", label: "sevenths" },
  { id: "seventhInversions", label: "seventh inversions" },
  { id: "progression", label: "progression" },
  { id: "exercise21", label: "the 21 exercise" },
];

const STYLE_OPTIONS = [
  { id: "block", label: "block" },
  { id: "arpeggio", label: "arpeggio" },
  { id: "ladder", label: "run it up" },
];

const INVERSION_LABELS = ["root", "1st", "2nd", "3rd"];

// One-line description of a saved deck's config, preset-blurb style.
const deckSummary = (cfg) => {
  const opt = CONTENT_OPTIONS.find((c) => c.id === cfg.content);
  const what =
    cfg.content === "progression" && cfg.progressionText
      ? `progression ${cfg.progressionText}`
      : (opt && opt.label) || String(cfg.content);
  const where =
    cfg.keys === "circle"
      ? "around the circle"
      : `in ${(Array.isArray(cfg.keys) ? cfg.keys : []).join(", ")}`;
  return `${what} ${where}`;
};

/* ---------- tiny shared UI ---------- */

const Chip = ({ on, onClick, children, disabled = false, ...rest }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-pressed={on}
    {...rest}
    className={cn(
      "rounded-full border px-3 py-1.5 text-sm transition-colors",
      on
        ? "border-indigo-400 bg-indigo-500/25 text-indigo-100"
        : "border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500",
      disabled && "cursor-not-allowed opacity-35 hover:border-slate-700"
    )}
  >
    {children}
  </button>
);

const CheckRow = ({ on, onChange, children, disabled = false }) => (
  <label
    className={cn(
      "flex cursor-pointer select-none items-center gap-2 text-sm text-slate-300",
      disabled && "cursor-not-allowed opacity-40"
    )}
  >
    <input
      type="checkbox"
      checked={on}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 accent-indigo-500"
    />
    {children}
  </label>
);

const FieldLabel = ({ children }) => (
  <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">{children}</p>
);

// Toggle an item in an array-valued piece of state, keeping it sorted.
const toggleIn = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value].sort((a, b) => a - b);

/* ---------- the page ---------- */

export default function ChordCaller() {
  const init = useMemo(() => loadSetup(), []);

  // Build-your-own deck config.
  const [selKeys, setSelKeys] = useState(init.keys);
  const [circle, setCircle] = useState(init.circle);
  const [content, setContent] = useState(init.content);
  const [degrees, setDegrees] = useState(init.degrees);
  const [inversions, setInversions] = useState(init.inversions);
  const [progressionText, setProgressionText] = useState(init.progressionText);
  const [order, setOrder] = useState(init.order);
  const [style, setStyle] = useState(init.style);
  const [bass, setBass] = useState(init.bass);
  const [presetKey, setPresetKey] = useState(init.presetKey);
  const [builderOpen, setBuilderOpen] = useState(false);

  // Saved decks ("your decks" on the setup screen).
  const [savedDecks, setSavedDecks] = useState(loadSavedDecks);
  const [deckName, setDeckName] = useState("");

  // Session settings.
  const [paceMode, setPaceMode] = useState(init.paceMode);
  const [paceSeconds, setPaceSeconds] = useState(init.paceSeconds);
  const [grooveBpm, setGrooveBpm] = useState(init.grooveBpm);
  const [grooveBars, setGrooveBars] = useState(init.grooveBars);
  const [alwaysHint, setAlwaysHint] = useState(init.alwaysHint);
  const [midiAdvance, setMidiAdvance] = useState(init.midiAdvance);
  const [strictBass, setStrictBass] = useState(init.strictBass);
  const [speakCalls, setSpeakCalls] = useState(init.speakCalls);

  // Session state.
  const [session, setSession] = useState(null); // { cards, config } | null
  const [idx, setIdx] = useState(0);
  const [lap, setLap] = useState(1);
  const [phase, setPhase] = useState("card"); // "card" | "done"
  const [paused, setPaused] = useState(false);
  const [manualHint, setManualHint] = useState(false);
  const [flash, setFlash] = useState(false);
  const [timerNonce, setTimerNonce] = useState(0);

  const rootRef = useRef(null);

  // Live refs so callbacks stay referentially stable while always seeing
  // current state (same idiom as PianoDrills).
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const idxRef = useRef(idx);
  idxRef.current = idx;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const midiAdvanceRef = useRef(midiAdvance);
  midiAdvanceRef.current = midiAdvance;
  const strictBassRef = useRef(strictBass);
  strictBassRef.current = strictBass;
  const paceModeRef = useRef(paceMode);
  paceModeRef.current = paceMode;

  const grooveOn = paceMode === "groove";

  /* ----- persist the last config + pace ----- */

  useEffect(() => {
    saveSetup({
      keys: selKeys,
      circle,
      content,
      degrees,
      inversions,
      progressionText,
      order,
      style,
      bass,
      presetKey,
      paceMode,
      paceSeconds,
      grooveBpm,
      grooveBars,
      alwaysHint,
      midiAdvance,
      strictBass,
      speakCalls,
    });
  }, [
    selKeys,
    circle,
    content,
    degrees,
    inversions,
    progressionText,
    order,
    style,
    bass,
    presetKey,
    paceMode,
    paceSeconds,
    grooveBpm,
    grooveBars,
    alwaysHint,
    midiAdvance,
    strictBass,
    speakCalls,
  ]);

  /* ----- custom deck preview ----- */

  const isSeventhContent = content === "sevenths" || content === "seventhInversions";
  const needsInversions = content === "inversions" || content === "seventhInversions";
  const maxInv = isSeventhContent ? 3 : 2;

  const customConfig = useMemo(() => {
    const cfg = { content, order, style, bass };
    cfg.keys = circle ? "circle" : selKeys;
    if (content !== "progression") cfg.degrees = degrees;
    if (needsInversions) cfg.inversions = inversions.filter((i) => i <= maxInv);
    if (content === "progression") cfg.progressionText = progressionText;
    return cfg;
  }, [
    selKeys,
    circle,
    content,
    degrees,
    inversions,
    progressionText,
    order,
    style,
    bass,
    needsInversions,
    maxInv,
  ]);

  const preview = useMemo(() => buildDeck(customConfig), [customConfig]);

  /* ----- saved decks ----- */

  const savedCounts = useMemo(
    () => savedDecks.map((d) => buildDeck(d.config).cards.length),
    [savedDecks]
  );

  const decksFull = savedDecks.length >= MAX_SAVED_DECKS;

  const saveDeck = () => {
    const name = deckName.trim();
    if (!name || preview.error || decksFull) return;
    const deck = {
      id: `d${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      config: customConfig,
    };
    setSavedDecks((prev) => {
      const next = [...prev, deck];
      saveSavedDecks(next);
      return next;
    });
    setDeckName("");
  };

  const removeDeck = (id) => {
    setSavedDecks((prev) => {
      const next = prev.filter((d) => d.id !== id);
      saveSavedDecks(next);
      return next;
    });
  };

  /* ----- starting / driving a session ----- */

  const start = useCallback((config) => {
    const { cards, error } = buildDeck(config);
    if (error || !cards.length) return;
    ensureAudio(); // user gesture — unlock audio for later ticks/blips
    setSession({ cards, config });
    setIdx(0);
    setLap(1);
    setPhase("card");
    setPaused(false);
    setManualHint(false);
    setFlash(false);
    setTimerNonce((n) => n + 1);
  }, []);

  // Starting a preset also hydrates the builder, so "last config" persists.
  const applyConfigToBuilder = (cfg) => {
    setCircle(cfg.keys === "circle");
    if (Array.isArray(cfg.keys)) setSelKeys(cfg.keys);
    setContent(cfg.content);
    setDegrees(cfg.degrees || ALL_DEGREES);
    const sev = cfg.content === "sevenths" || cfg.content === "seventhInversions";
    setInversions(cfg.inversions || (sev ? [0, 1, 2, 3] : [0, 1, 2]));
    if (cfg.progressionText) setProgressionText(cfg.progressionText);
    setOrder(cfg.order || "inOrder");
    setStyle(cfg.style || "block");
    setBass(!!cfg.bass);
  };

  const advance = useCallback(() => {
    const s = sessionRef.current;
    if (!s || phaseRef.current !== "card") return;
    setManualHint(false);
    setFlash(false);
    if (idxRef.current + 1 >= s.cards.length) setPhase("done");
    else setIdx(idxRef.current + 1);
  }, []);

  const back = useCallback(() => {
    if (!sessionRef.current) return;
    setManualHint(false);
    setFlash(false);
    if (phaseRef.current === "done") setPhase("card");
    else setIdx((i) => Math.max(0, i - 1));
  }, []);

  const again = useCallback(() => {
    if (!sessionRef.current) return;
    setLap((l) => l + 1);
    setIdx(0);
    setPhase("card");
    setManualHint(false);
    setFlash(false);
  }, []);

  const reshuffle = () => {
    const s = session;
    if (!s) return;
    const { cards } = buildDeck(s.config);
    if (cards.length) setSession({ ...s, cards });
    setLap((l) => l + 1);
    setIdx(0);
    setPhase("card");
    setManualHint(false);
  };

  const exit = useCallback(() => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    } catch {
      /* fullscreen not supported — fine */
    }
    setSession(null);
    setPhase("card");
    setIdx(0);
    setLap(1);
    setPaused(false);
  }, []);

  /* ----- keyboard: space/enter/→ advance, ← back ----- */

  useEffect(() => {
    if (!session) return undefined;
    const down = (e) => {
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      // Groove mode: the metronome owns the advance — no manual navigation
      // mid-card (the lap interstitial's keys still work).
      if (paceModeRef.current === "groove" && phaseRef.current === "card") return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
        return;
      }
      if (e.key === " " || e.key === "Enter" || e.key === "ArrowRight") {
        if (tag === "BUTTON") return; // let a focused button be a button
        e.preventDefault();
        if (phaseRef.current === "done") again();
        else advance();
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [session, advance, back, again]);

  /* ----- timer mode: auto-advance + soft tick ----- */

  useEffect(() => {
    if (!session || paceMode !== "timer" || paused) return undefined;
    const t = setTimeout(phase === "card" ? advance : again, paceSeconds * 1000);
    return () => clearTimeout(t);
  }, [session, idx, lap, phase, paused, paceMode, paceSeconds, timerNonce, advance, again]);

  useEffect(() => {
    if (!session || phase !== "card" || paceMode !== "timer") return;
    playTick();
  }, [session, idx, lap, phase, paceMode]);

  /* ----- groove mode: 4-beat count-in, then the metronome owns the advance ----- */

  const [grooveNonce, setGrooveNonce] = useState(0); // bump → restart with a fresh count-in
  // { countIn: true, count: 1..4 } | { countIn: false, beat: 0..3 } | null
  const [grooveBeat, setGrooveBeat] = useState(null);

  useEffect(() => {
    if (!session || !grooveOn || phase !== "card") {
      setGrooveBeat(null);
      return undefined;
    }
    // Lookahead scheduler: a short interval schedules clicks ~0.1s ahead on
    // the audio clock; UI updates (dots, card advance) ride setTimeout so
    // they land when the click actually sounds. Falls back to the wall clock
    // (silent metronome) where Web Audio doesn't exist.
    const haveAudio = ensureAudio();
    const now = () => (haveAudio ? audioNow() : performance.now() / 1000);
    const secPerBeat = 60 / grooveBpm;
    const beatsPerCard = 4 * grooveBars;
    let nextTime = now() + 0.2;
    let beatIndex = -4; // four count-in beats before beat 0 of the first bar
    let stopped = false;
    let interval = null;
    const timeouts = new Set();

    const uiAt = (atSec, fn) => {
      const t = setTimeout(() => {
        timeouts.delete(t);
        if (!stopped) fn();
      }, Math.max(0, (atSec - now()) * 1000));
      timeouts.add(t);
    };

    const halt = () => {
      stopped = true;
      if (interval) clearInterval(interval);
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };

    const tick = () => {
      while (nextTime < now() + 0.1) {
        const i = beatIndex;
        const beatInBar = ((i % 4) + 4) % 4;
        if (haveAudio) playClickAt(nextTime, beatInBar === 0);
        uiAt(nextTime, () =>
          setGrooveBeat(i < 0 ? { countIn: true, count: i + 5 } : { countIn: false, beat: beatInBar })
        );
        if (i > 0 && beatInBar === 0 && i % beatsPerCard === 0) uiAt(nextTime, advance);
        nextTime += secPerBeat;
        beatIndex += 1;
      }
    };
    tick();
    interval = setInterval(tick, 25);

    // Hidden tab → stop dead; on return, restart with a fresh count-in
    // rather than pretending we kept phase.
    const onVis = () => {
      if (document.visibilityState === "hidden") halt();
      else if (stopped) setGrooveNonce((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      halt();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [session, lap, phase, grooveOn, grooveBpm, grooveBars, grooveNonce, advance]);

  /* ----- speak the calls (SpeechSynthesis) ----- */

  useEffect(() => {
    if (!speechSupported) return;
    if (!session || phase !== "card" || !speakCalls) {
      window.speechSynthesis.cancel();
      return;
    }
    const c = session.cards[idx];
    if (!c) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(c.speech || c.title);
    u.rate = 1.0;
    window.speechSynthesis.speak(u);
  }, [session, idx, lap, phase, speakCalls]);

  useEffect(
    () => () => {
      if (speechSupported) window.speechSynthesis.cancel();
    },
    []
  );

  /* ----- MIDI: listen always, advance on a matching chord ----- */

  const heldRef = useRef(new Set());
  const accumRef = useRef(new Set()); // pitch classes since this card appeared
  const matchedRef = useRef(false);

  useEffect(() => {
    accumRef.current = new Set();
    matchedRef.current = false;
    heldRef.current = new Set();
  }, [session, idx, lap, phase]);

  const press = useCallback(
    (midi) => {
      heldRef.current.add(midi);
      const s = sessionRef.current;
      if (!s || phaseRef.current !== "card" || !midiAdvanceRef.current || matchedRef.current) {
        return;
      }
      accumRef.current.add(((midi % 12) + 12) % 12);
      const card = s.cards[idxRef.current];
      if (!card) return;
      const deckStyle = s.config.style || "block";
      const ok =
        deckStyle === "block"
          ? matchesCard([...heldRef.current], card, strictBassRef.current)
          : card.pcs.every((pc) => accumRef.current.has(pc)); // running it up — bass is wherever you are
      if (!ok) return; // not yet — and never a penalty
      matchedRef.current = true;
      playSuccess();
      setFlash(true);
      // In groove mode the metronome owns the advance — the flash is the
      // whole reward; the card changes on its downbeat regardless.
      if (paceModeRef.current !== "groove") setTimeout(advance, 350); // let the green flash land
    },
    [advance]
  );

  const release = useCallback((midi) => {
    heldRef.current.delete(midi);
  }, []);

  const { status: midiStatus, inputs: midiInputs } = useMidiInput(press, release, true);
  const midiPresent = midiStatus === "ready" && midiInputs.length > 0;

  /* ----- wake lock: hold the screen awake during a session ----- */

  const wakeRef = useRef(null);
  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    const acquire = async () => {
      try {
        if (navigator.wakeLock && document.visibilityState === "visible") {
          const sentinel = await navigator.wakeLock.request("screen");
          if (cancelled) sentinel.release().catch(() => {});
          else wakeRef.current = sentinel;
        }
      } catch {
        /* unsupported or denied — silently practice without it */
      }
    };
    acquire();
    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try {
        if (wakeRef.current) wakeRef.current.release().catch(() => {});
      } catch {
        /* already released */
      }
      wakeRef.current = null;
    };
  }, [session]);

  /* ----- fullscreen ----- */

  const toggleFullscreen = () => {
    try {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      } else if (rootRef.current && rootRef.current.requestFullscreen) {
        rootRef.current.requestFullscreen().catch(() => {});
      }
    } catch {
      /* fullscreen not supported — fine */
    }
  };

  /* ---------- render ---------- */

  const card = session ? session.cards[idx] : null;
  const hintShown = alwaysHint || manualHint;
  const timed = paceMode === "timer";

  return (
    <div ref={rootRef} className="min-h-screen bg-slate-950 text-slate-100">
      {!session ? (
        /* ================= setup screen ================= */
        <div className="mx-auto max-w-3xl px-4 pb-16 pt-8">
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex gap-4 text-sm">
              <Link
                to="/sillyprojects"
                className="text-slate-500 underline-offset-2 hover:text-slate-300 hover:underline"
              >
                ← silly projects
              </Link>
              <Link
                to="/piano-drills"
                className="text-slate-500 underline-offset-2 hover:text-slate-300 hover:underline"
              >
                piano drills →
              </Link>
            </div>
          </div>

          <header className="mt-8 text-center">
            <h1 className="text-4xl font-bold tracking-tight">
              Chord Caller <span aria-hidden>📣</span>
            </h1>
            <p className="mt-2 text-slate-400">
              Prop this on the music stand — the screen calls chords, you play them.
            </p>
          </header>

          {/* preset key selector — governs the key-scoped presets only */}
          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-sm text-slate-400">call in the key of</span>
              {KEYS.map((k) => (
                <Chip
                  key={k.name}
                  data-preset-key={k.name}
                  on={presetKey === k.name}
                  onClick={() => setPresetKey(k.name)}
                >
                  {k.name}
                </Chip>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              the “around the circle” decks visit all 12 keys and ignore this
            </p>
          </div>

          {/* presets */}
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PRESET_DECKS.map((p, i) => (
              <button
                key={p.id}
                type="button"
                data-preset={p.id}
                onClick={() => {
                  const cfg = presetConfigForKey(p, presetKey);
                  applyConfigToBuilder(cfg);
                  start(cfg);
                }}
                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-left transition-colors hover:border-indigo-500/60 hover:bg-slate-900"
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-slate-100">{p.label}</span>
                  <span className="shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                    {PRESET_COUNTS[i]} cards
                  </span>
                </span>
                <span className="mt-1.5 block text-sm leading-snug text-slate-400">
                  {presetBlurb(p, presetKey)}
                </span>
                {p.hint && (
                  <span className="mt-1.5 block text-xs italic leading-snug text-slate-500">
                    {p.hint}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* your decks — saved from the builder; they carry their own keys */}
          {savedDecks.length > 0 && (
            <div className="mt-8">
              <FieldLabel>Your decks</FieldLabel>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {savedDecks.map((d, i) => (
                  <div key={d.id} className="relative">
                    <button
                      type="button"
                      data-deck={d.id}
                      onClick={() => {
                        applyConfigToBuilder(d.config);
                        start(d.config);
                      }}
                      className="h-full w-full rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-left transition-colors hover:border-indigo-500/60 hover:bg-slate-900"
                    >
                      <span className="flex items-baseline justify-between gap-2 pr-6">
                        <span className="font-semibold text-slate-100">{d.name}</span>
                        <span className="shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                          {savedCounts[i]} cards
                        </span>
                      </span>
                      <span className="mt-1.5 block text-sm leading-snug text-slate-400">
                        {deckSummary(d.config)}
                      </span>
                    </button>
                    <button
                      type="button"
                      data-deck-delete={d.id}
                      aria-label={`Delete deck ${d.name}`}
                      onClick={() => removeDeck(d.id)}
                      className="absolute right-2 top-2 rounded-full px-1.5 text-sm text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                saved with their own keys — the “call in the key of” row doesn’t change these
              </p>
            </div>
          )}

          {/* pace + session settings */}
          <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
            <FieldLabel>Pace</FieldLabel>
            <div className="flex flex-wrap items-center gap-3">
              <Chip on={paceMode === "self"} onClick={() => setPaceMode("self")}>
                self-paced
              </Chip>
              <Chip on={timed} onClick={() => setPaceMode("timer")}>
                timed
              </Chip>
              <Chip on={grooveOn} onClick={() => setPaceMode("groove")}>
                groove
              </Chip>
              {timed && (
                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input
                    type="range"
                    min={2}
                    max={15}
                    step={1}
                    value={paceSeconds}
                    aria-label="Seconds per chord"
                    onChange={(e) => setPaceSeconds(Number(e.target.value))}
                    className="w-40 accent-indigo-500"
                  />
                  <span className="w-20 tabular-nums">{paceSeconds}s / chord</span>
                </label>
              )}
              {grooveOn && (
                <>
                  <label className="flex items-center gap-3 text-sm text-slate-300">
                    <input
                      type="range"
                      min={40}
                      max={140}
                      step={1}
                      value={grooveBpm}
                      aria-label="Beats per minute"
                      onChange={(e) => setGrooveBpm(Number(e.target.value))}
                      className="w-40 accent-indigo-500"
                    />
                    <span className="w-20 tabular-nums">{grooveBpm} bpm</span>
                  </label>
                  <div className="flex gap-1.5">
                    <Chip on={grooveBars === 1} onClick={() => setGrooveBars(1)}>
                      1 bar / chord
                    </Chip>
                    <Chip on={grooveBars === 2} onClick={() => setGrooveBars(2)}>
                      2 bars / chord
                    </Chip>
                  </div>
                </>
              )}
              {paceMode === "self" && (
                <span className="text-xs text-slate-500">
                  tap the card (or space / →) to advance
                </span>
              )}
            </div>
            {grooveOn && (
              <p className="mt-2 text-xs text-slate-500">
                four beats count you in, then the chord changes on the downbeat — the metronome
                is the boss
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
              <CheckRow on={alwaysHint} onChange={setAlwaysHint}>
                always show hints
              </CheckRow>
              <CheckRow on={midiAdvance} onChange={setMidiAdvance}>
                advance when you play it{midiPresent ? "" : " (needs MIDI)"}
              </CheckRow>
              <CheckRow on={strictBass} onChange={setStrictBass} disabled={!midiAdvance}>
                strict bass note
              </CheckRow>
              {speechSupported && (
                <CheckRow on={speakCalls} onChange={setSpeakCalls}>
                  speak the calls
                </CheckRow>
              )}
            </div>
            {midiPresent && (
              <p className="mt-3 text-xs text-emerald-400">
                🎹 MIDI connected: {midiInputs.join(", ")}
              </p>
            )}
          </div>

          {/* build your own */}
          <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/50">
            <button
              type="button"
              onClick={() => setBuilderOpen((o) => !o)}
              aria-expanded={builderOpen}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-200"
            >
              build your own deck
              <span aria-hidden className="text-slate-500">
                {builderOpen ? "▾" : "▸"}
              </span>
            </button>

            {builderOpen && (
              <div className="space-y-4 border-t border-slate-800 p-4">
                <div>
                  <FieldLabel>Keys</FieldLabel>
                  <div className="flex flex-wrap gap-1.5">
                    <Chip on={circle} onClick={() => setCircle((c) => !c)}>
                      around the circle (all 12)
                    </Chip>
                    {KEYS.map((k) => (
                      <Chip
                        key={k.name}
                        on={!circle && selKeys.includes(k.name)}
                        disabled={circle}
                        onClick={() =>
                          setSelKeys((prev) =>
                            prev.includes(k.name)
                              ? prev.filter((n) => n !== k.name)
                              : [...prev, k.name]
                          )
                        }
                      >
                        {k.name}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <FieldLabel>Call</FieldLabel>
                  <div className="flex flex-wrap gap-1.5">
                    {CONTENT_OPTIONS.map((c) => (
                      <Chip
                        key={c.id}
                        on={content === c.id}
                        onClick={() => {
                          setContent(c.id);
                          // Triad contents can't use a 3rd inversion.
                          if (c.id === "inversions") {
                            setInversions((prev) => {
                              const kept = prev.filter((i) => i <= 2);
                              return kept.length ? kept : [0, 1, 2];
                            });
                          }
                        }}
                      >
                        {c.label}
                      </Chip>
                    ))}
                  </div>
                </div>

                {content !== "progression" && content !== "exercise21" && (
                  <div>
                    <FieldLabel>Degrees</FieldLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_DEGREES.map((d) => (
                        <Chip
                          key={d}
                          on={degrees.includes(d)}
                          onClick={() => setDegrees((prev) => toggleIn(prev, d))}
                        >
                          {d}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {needsInversions && (
                  <div>
                    <FieldLabel>Inversions</FieldLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {INVERSION_LABELS.slice(0, maxInv + 1).map((label, i) => (
                        <Chip
                          key={label}
                          on={inversions.includes(i)}
                          onClick={() => setInversions((prev) => toggleIn(prev, i))}
                        >
                          {label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {content === "progression" && (
                  <div>
                    <FieldLabel>Progression (scale degrees)</FieldLabel>
                    <input
                      type="text"
                      value={progressionText}
                      onChange={(e) => setProgressionText(e.target.value)}
                      aria-label="Progression degrees"
                      placeholder="e.g. 2 5 1"
                      className="w-full max-w-xs rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-indigo-400 focus:outline-none"
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  <div>
                    <FieldLabel>Order</FieldLabel>
                    <div className="flex gap-1.5">
                      <Chip on={order === "inOrder"} onClick={() => setOrder("inOrder")}>
                        in order
                      </Chip>
                      <Chip on={order === "shuffle"} onClick={() => setOrder("shuffle")}>
                        shuffle
                      </Chip>
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Style</FieldLabel>
                    <div className="flex gap-1.5">
                      {STYLE_OPTIONS.map((s) => (
                        <Chip key={s.id} on={style === s.id} onClick={() => setStyle(s.id)}>
                          {s.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Left hand</FieldLabel>
                    <Chip on={bass} onClick={() => setBass((b) => !b)}>
                      call a bass note below
                    </Chip>
                  </div>
                </div>

                {preview.error ? (
                  <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
                    {preview.error}
                  </p>
                ) : (
                  <p className="text-sm text-slate-400">
                    {preview.cards.length} card{preview.cards.length === 1 ? "" : "s"} ready.
                  </p>
                )}

                <button
                  type="button"
                  disabled={!!preview.error}
                  onClick={() => start(customConfig)}
                  className={cn(
                    "w-full rounded-xl px-4 py-3 text-base font-semibold transition-colors",
                    preview.error
                      ? "cursor-not-allowed bg-slate-800 text-slate-500"
                      : "bg-indigo-500 text-white hover:bg-indigo-400"
                  )}
                >
                  Start calling
                </button>

                {/* save this deck → "your decks" above */}
                <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-4">
                  <input
                    type="text"
                    value={deckName}
                    onChange={(e) => setDeckName(e.target.value)}
                    aria-label="Deck name"
                    placeholder="name this deck"
                    className="w-full max-w-[14rem] rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-indigo-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    data-cc="save-deck"
                    disabled={!deckName.trim() || !!preview.error || decksFull}
                    onClick={saveDeck}
                    className={cn(
                      "rounded-lg border px-4 py-1.5 text-sm transition-colors",
                      !deckName.trim() || preview.error || decksFull
                        ? "cursor-not-allowed border-slate-800 text-slate-600"
                        : "border-indigo-500/60 text-indigo-200 hover:bg-indigo-500/15"
                    )}
                  >
                    save this deck
                  </button>
                  {decksFull ? (
                    <p className="w-full text-xs text-amber-300">
                      deck box is full ({MAX_SAVED_DECKS}) — delete one to save another
                    </p>
                  ) : (
                    <p className="w-full text-xs text-slate-500">
                      saves this setup to “your decks”, keys included
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ================= session screen ================= */
        <div className="flex h-screen flex-col">
          <style>{`@keyframes ccDrain { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}</style>

          {/* top bar */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400">
            <button
              type="button"
              data-cc="exit"
              onClick={exit}
              className="rounded-lg border border-slate-800 px-3 py-1 hover:border-slate-600 hover:text-slate-200"
            >
              ✕ exit
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label="Toggle fullscreen"
              className="rounded-lg border border-slate-800 px-3 py-1 hover:border-slate-600 hover:text-slate-200"
            >
              ⛶
            </button>
            {timed && phase === "card" && (
              <button
                type="button"
                onClick={() => {
                  setPaused((p) => !p);
                  if (paused) setTimerNonce((n) => n + 1); // resuming restarts the card timer
                }}
                className="rounded-lg border border-slate-800 px-3 py-1 hover:border-slate-600 hover:text-slate-200"
              >
                {paused ? "▶ resume" : "⏸ pause"}
              </button>
            )}
            {grooveOn && (
              <span data-cc="groove-label" className="text-xs tabular-nums text-slate-500">
                {grooveBpm} bpm · {grooveBars === 1 ? "1 bar" : "2 bars"} / chord
              </span>
            )}
            {midiPresent && (
              <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                🎹 MIDI
              </span>
            )}
            <span className="ml-auto text-right leading-tight">
              <span data-cc="progress" className="block tabular-nums text-slate-300">
                {idx + 1} / {session.cards.length}
              </span>
              <span className="block text-xs text-slate-500">lap {lap}</span>
            </span>
          </div>

          {phase === "card" && card ? (
            /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
            <div
              data-cc="card"
              role="button"
              tabIndex={-1}
              onClick={grooveOn ? undefined : advance}
              className={cn(
                "flex min-h-0 flex-1 select-none flex-col items-center justify-center px-4 text-center transition-colors duration-300",
                grooveOn ? "cursor-default" : "cursor-pointer",
                flash && "bg-emerald-500/15"
              )}
            >
              <div
                data-cc="title"
                className="font-bold leading-none tracking-tight"
                style={{ fontSize: "clamp(3.5rem, 18vw, 14rem)" }}
              >
                {card.title}
              </div>
              {card.subtitle && (
                <p data-cc="subtitle" className="mt-3 text-lg text-slate-400 sm:text-xl">
                  {card.subtitle}
                </p>
              )}
              {card.instruction && (
                <p className="mt-1 text-base text-indigo-300 sm:text-lg">{card.instruction}</p>
              )}

              <div className="mt-5 flex min-h-[7rem] flex-col items-center justify-start gap-2">
                <button
                  type="button"
                  data-cc="hint-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setManualHint((h) => !h);
                  }}
                  className="rounded-full border border-slate-700 px-4 py-1 text-sm text-slate-400 hover:border-slate-500 hover:text-slate-200"
                >
                  {hintShown ? "hide hint" : "hint"}
                </button>
                {hintShown && (
                  /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
                  <div
                    data-cc="hint"
                    role="note"
                    onClick={(e) => e.stopPropagation()}
                    className="flex cursor-default flex-col items-center gap-1.5"
                  >
                    <span className="inline-block rounded-lg bg-white/5 p-2 [&_svg]:h-auto [&_svg]:w-56">
                      <MiniChordDiagram midis={card.midis} />
                    </span>
                    <span className="text-lg tracking-wide text-slate-300">
                      {card.noteNames.join(" · ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* lap interstitial */
            <div
              data-cc="lap-done"
              className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-4 text-center"
            >
              <div>
                <p className="text-5xl font-bold sm:text-6xl">Lap {lap} done ✨</p>
                <p className="mt-3 text-slate-400">
                  {session.cards.length} chords called. Shake out your hands.
                </p>
                {timed && !paused && (
                  <p className="mt-1 text-sm text-slate-500">going again in {paceSeconds}s…</p>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  data-cc="again"
                  onClick={again}
                  className="rounded-xl bg-indigo-500 px-6 py-3 font-semibold text-white hover:bg-indigo-400"
                >
                  Again
                </button>
                {session.config.order === "shuffle" && (
                  <button
                    type="button"
                    onClick={reshuffle}
                    className="rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 hover:border-slate-500"
                  >
                    Reshuffle
                  </button>
                )}
                <button
                  type="button"
                  onClick={exit}
                  className="rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 hover:border-slate-500"
                >
                  Change deck
                </button>
              </div>
            </div>
          )}

          {/* beat dots (groove mode) — the metronome made visible */}
          {grooveOn && phase === "card" && (
            <div data-cc="beatdots" className="flex h-12 items-center justify-center gap-3 pb-4">
              {grooveBeat && grooveBeat.countIn && (
                <span
                  data-cc="countin"
                  className="mr-1 text-sm font-semibold tabular-nums text-amber-300"
                >
                  count-in · {grooveBeat.count}
                </span>
              )}
              {[0, 1, 2, 3].map((b) => {
                const active = grooveBeat
                  ? grooveBeat.countIn
                    ? grooveBeat.count - 1 >= b // count-in fills the dots 1 → 4
                    : grooveBeat.beat === b
                  : false;
                return (
                  <span
                    key={b}
                    data-cc={`beat-${b}`}
                    className={cn(
                      "rounded-full transition-all duration-150",
                      b === 0 ? "h-4 w-4" : "h-3 w-3", // beat 1 (the accent) is the big dot
                      active
                        ? grooveBeat.countIn
                          ? "bg-amber-400"
                          : b === 0
                            ? "bg-indigo-300 ring-4 ring-indigo-500/30"
                            : "bg-indigo-400"
                        : "bg-slate-700"
                    )}
                  />
                );
              })}
            </div>
          )}

          {/* countdown bar (timer mode) */}
          {timed && phase === "card" && (
            <div className="h-1 w-full bg-slate-900">
              <div
                key={`${lap}-${idx}-${timerNonce}`}
                data-cc="countbar"
                className="h-1 origin-left bg-indigo-400/80"
                style={{
                  animation: `ccDrain ${paceSeconds}s linear forwards`,
                  animationPlayState: paused ? "paused" : "running",
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
